import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CA_PROVINCES, HOME_COUNTRIES, HOME_POSTAL_MAX_LENGTH, checkHomePostal, checkHomeRegion,
  homeRegionsFor, normalizePostalCode, postalProvinceMismatch, resolveHomeRegion,
  type HomeCountry,
} from '@evi/shared';
import { formatOrList, homeAddressErrorText, type HomeAddressDraft } from '@/lib/homeAddressDraft';
import formStyles from '@/components/Form.module.css';
import styles from './HomeAddressFields.module.css';

type Field = 'state' | 'zip';

/**
 * The home address, country first (item 5, 2026-10-02). Warren: "Postal
 * code: text input accepting Canadian postal codes and US ZIPs, validated
 * by country."
 *
 * United States / Canada pills with no preselection; the state or province
 * comes from a list for that country (typed by code or name); the postal
 * field is labelled, sized and keyboarded by country (numeric for a ZIP,
 * capitals for a Canadian code). Switching from one country to the other
 * clears the region and the postal code, and switching back to the saved
 * country brings the saved ones back; choosing a country for the first
 * time keeps what was typed. Once a change of country would be refused
 * (the home has a charger or a payout account), the other pill is held
 * still with a note saying why. The rules are @evi/shared's
 * homeAddress.ts, the same ones POST and PATCH /me/home apply, so a form
 * that validates here is one the Api accepts.
 *
 * Used by Set up My Home (MyHomePage) and Home Settings' Home profile.
 */

export function HomeAddressFields({
  idPrefix, value, onChange, disabled, showErrors, initial, countryLocked, countryLockAction,
}: {
  /** Prefix for the input ids, unique per form on the page. */
  idPrefix: string;
  value: HomeAddressDraft;
  onChange: (next: HomeAddressDraft) => void;
  disabled?: boolean;
  /** Show every field's error now, touched or not (after a refused save). */
  showErrors?: boolean;
  /** The saved address (Home Settings): switching back to its country
   *  restores its region and postal code. */
  initial?: HomeAddressDraft;
  /** The saved country can no longer change in the app (GET /me/home
   *  country_locked): the other country's pill is disabled. */
  countryLocked?: boolean;
  /** Shown under the lock note, which tells the host to contact support:
   *  Home Settings passes its Contact support button here. */
  countryLockAction?: ReactNode;
}) {
  const { t, i18n } = useTranslation('homeAddress');
  // A field's error shows once the host has left it, never while typing.
  const [touched, setTouched] = useState<Record<Field, boolean>>({ state: false, zip: false });
  const country = value.country;
  const ca = country === 'CA';
  const set = (patch: Partial<HomeAddressDraft>) => onChange({ ...value, ...patch });

  // Held only for a home that has a saved country; one that never had
  // one is the server's call (a first setting may still be allowed).
  const lockedTo = countryLocked && initial?.country ? initial.country : null;

  const chooseCountry = (next: HomeCountry): void => {
    if (next === country || (lockedTo && next !== lockedTo)) return;
    if (country === null) {
      set({ country: next });
    } else if (initial && next === initial.country) {
      // Back to the saved country: its region and postal code return.
      set({ country: next, state: initial.state, zip: initial.zip });
      setTouched({ state: false, zip: false });
    } else {
      // Switching countries: the other country's region and postal code
      // cannot carry over.
      set({ country: next, state: '', zip: '' });
      setTouched({ state: false, zip: false });
    }
  };

  const regionCheck = country ? checkHomeRegion(country, value.state) : null;
  const postalCheck = country ? checkHomePostal(country, value.zip) : null;
  const regionError = (touched.state || showErrors) && regionCheck && !regionCheck.ok
    ? homeAddressErrorText(t, regionCheck.code, country, value.state)
    : null;
  const postalError = (touched.zip || showErrors) && postalCheck && !postalCheck.ok
    ? homeAddressErrorText(t, postalCheck.code, country)
    : null;

  // Soft: a Canadian code whose first letter belongs elsewhere.
  const mismatch = country && regionCheck?.ok && postalCheck?.ok
    ? postalProvinceMismatch(country, postalCheck.zip, regionCheck.region.code)
    : null;
  const mismatchNames = mismatch
    ? formatOrList(
        mismatch.expected.map((code) => CA_PROVINCES.find((p) => p.code === code)?.name ?? code),
        i18n.language,
      )
    : '';

  // Leaving a field tidies it into the stored form (on -> Ontario,
  // k1a0b1 -> K1A 0B1) when it is valid; an invalid value stays as typed.
  const blurRegion = (): void => {
    setTouched((s) => ({ ...s, state: true }));
    const region = country ? resolveHomeRegion(value.state, country) : null;
    if (region && region.name !== value.state) set({ state: region.name });
  };
  const blurPostal = (): void => {
    setTouched((s) => ({ ...s, zip: true }));
    const normalized = country ? normalizePostalCode(country, value.zip) : null;
    if (normalized && normalized !== value.zip) set({ zip: normalized });
  };

  return (
    <div className={styles.fields}>
      <div className={formStyles.field}>
        <span className={formStyles.label} id={`${idPrefix}-country-label`}>{t('country.label')}</span>
        <div className={styles.pills} role="radiogroup" aria-labelledby={`${idPrefix}-country-label`}>
          {HOME_COUNTRIES.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={country === c}
              className={`${styles.pill} ${country === c ? styles.pillActive : ''}`}
              onClick={() => chooseCountry(c)}
              disabled={disabled || (lockedTo !== null && c !== lockedTo)}
              aria-describedby={lockedTo ? `${idPrefix}-country-locked` : undefined}
            >
              {c === 'CA' ? t('country.ca') : t('country.us')}
            </button>
          ))}
        </div>
        {!country && <p className={styles.fieldHint}>{t('country.hint')}</p>}
        {lockedTo && (
          <p id={`${idPrefix}-country-locked`} className={styles.fieldHint}>{t('country.locked')}</p>
        )}
        {lockedTo && countryLockAction}
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor={`${idPrefix}-street`}>{t('street.label')}</label>
        <input
          id={`${idPrefix}-street`} className={formStyles.input} value={value.street}
          onChange={(e) => set({ street: e.target.value })}
          placeholder={t('street.placeholder')} maxLength={200} disabled={disabled}
          autoComplete="address-line1" required
        />
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor={`${idPrefix}-city`}>{t('city.label')}</label>
        <input
          id={`${idPrefix}-city`} className={formStyles.input} value={value.city}
          onChange={(e) => set({ city: e.target.value })}
          placeholder={ca ? t('city.placeholderCa') : t('city.placeholderUs')}
          maxLength={120} disabled={disabled} autoComplete="address-level2" required
        />
      </div>

      {country && (
        // Keyed by country so the row replays its entrance on a switch.
        <div key={country} className={`${styles.fields} ${styles.reveal}`}>
          <div className={styles.row2}>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor={`${idPrefix}-state`}>
                {ca ? t('region.provinceLabel') : t('region.stateLabel')}
              </label>
              <input
                id={`${idPrefix}-state`} className={formStyles.input} value={value.state}
                onChange={(e) => set({ state: e.target.value })}
                onBlur={blurRegion}
                list={`${idPrefix}-regions`}
                placeholder={ca ? t('region.provincePlaceholder') : t('region.statePlaceholder')}
                maxLength={120} disabled={disabled} autoComplete="address-level1" required
                aria-invalid={regionError ? true : undefined}
                aria-describedby={regionError ? `${idPrefix}-state-error` : undefined}
              />
              <datalist id={`${idPrefix}-regions`}>
                {homeRegionsFor(country).map((r) => <option key={r.code} value={r.name} />)}
              </datalist>
            </div>
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor={`${idPrefix}-zip`}>
                {ca ? t('postal.postalLabel') : t('postal.zipLabel')}
              </label>
              <input
                id={`${idPrefix}-zip`} className={`${formStyles.input} ${styles.postal}`} value={value.zip}
                onChange={(e) => set({ zip: e.target.value })}
                onBlur={blurPostal}
                type="text"
                inputMode={ca ? 'text' : 'numeric'}
                autoCapitalize={ca ? 'characters' : 'off'}
                autoCorrect="off"
                spellCheck={false}
                autoComplete="postal-code"
                placeholder={ca ? t('postal.postalPlaceholder') : t('postal.zipPlaceholder')}
                maxLength={HOME_POSTAL_MAX_LENGTH[country]} disabled={disabled} required
                aria-invalid={postalError ? true : undefined}
                aria-describedby={postalError ? `${idPrefix}-zip-error` : undefined}
              />
            </div>
          </div>
          {regionError && <p id={`${idPrefix}-state-error`} className={styles.fieldError} role="alert">{regionError}</p>}
          {postalError && <p id={`${idPrefix}-zip-error`} className={styles.fieldError} role="alert">{postalError}</p>}
          {mismatch && !postalError && !regionError && (
            <p className={styles.fieldHint}>
              {t('postal.mismatch', { postal: postalCheck?.ok ? postalCheck.zip : value.zip, region: mismatchNames })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
